import { datadogRum } from '@datadog/browser-rum';
import { QueryClient } from '@tanstack/react-query';
import { createRouter } from '@tanstack/react-router';
import FullPageLoader from './components/FullPageLoader';
import { ResolvedAuth } from './contexts/AuthContext';
import NotFound from './pages/notFound/NotFound';
import { routeTree } from './routeTree.gen';
import { queryClient } from './services/queryClient';
import { trackPendoEvent } from './utils/pendo';
import { PermissionString } from './utils/permissions';

export interface AuthContext {
  isAuthenticated: boolean;
  isLoading: boolean;
  hasPermission: (permission: PermissionString) => boolean;
  login: (returnTo?: string) => void;
  logout: () => void;
  whenReady: () => Promise<ResolvedAuth>;
}

export interface RouterContext {
  queryClient: QueryClient;
  auth: AuthContext;
}

// Create the router instance
export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  context: {
    // Default auth context values to non-authenticated
    auth: {
      isAuthenticated: false,
      isLoading: true,
      hasPermission: () => false,
      login: () => {},
      logout: () => {},
      whenReady: () =>
        Promise.resolve({ isAuthenticated: false, hasPermission: () => false }),
    },
    queryClient,
  },
  defaultNotFoundComponent: NotFound,
  defaultPendingComponent: FullPageLoader,
});

// Track page views at router level. Each navigation produces one Datadog RUM
// view keyed by pathname or ddViewName.
router.subscribe('onResolved', ({ toLocation }) => {
  const routeMatches = router.state.matches;
  const ddViewName = routeMatches
    .map((match) => match.staticData?.ddViewName)
    .filter((name): name is string => Boolean(name))
    .pop(); // deepest match wins

  datadogRum.startView({
    name: ddViewName ?? toLocation.pathname,
    url: toLocation.pathname,
  });

  trackPendoEvent('page_view', {
    page_name: toLocation.pathname,
  });
});

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
  interface StaticDataRouteOption {
    ddViewName?: string;
  }
}
